import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import AppCard from '../AppCard';
import './multiScroller.css'; // you’ll thank me later

interface IProps {
    cards: Array<any>;
}

const MultiScroller: React.FC<IProps> = React.memo(({ cards }) => {
    // double the cards so it loops seamlessly
    const doubledCards = useMemo(() => [...cards, ...cards], [cards]);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [fitsWithoutLoop, setFitsWithoutLoop] = useState(false);

    // With only a few cards the row already fits: looping it would just slide them away and back.
    useLayoutEffect(() => {
        const scroller = scrollerRef.current;
        const firstSlide = scroller?.querySelector<HTMLElement>('.multi-slide');
        if (!scroller || !firstSlide) return;
        const measure = () => {
            const style = getComputedStyle(scroller);
            const available = scroller.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0');
            setFitsWithoutLoop(firstSlide.getBoundingClientRect().width * cards.length <= available);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(scroller);
        return () => observer.disconnect();
    }, [cards.length]);

    return (
        <div ref={scrollerRef} className={fitsWithoutLoop ? 'multi-scroller is-static' : 'multi-scroller'}>
            <div className="multi-track" role="list">
                {doubledCards.map((card, idx) => {
                    // The second copy only exists for the visual loop: hide it from assistive tech and the tab order
                    const isLoopCopy = idx >= cards.length;
                    return (
                        <div className="multi-slide" key={`${card.id}-${idx}`} role={isLoopCopy ? undefined : 'listitem'} aria-hidden={isLoopCopy || undefined}>
                            <AppCard type="top" details={card} tabIndex={isLoopCopy ? -1 : undefined} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

MultiScroller.displayName = 'MultiScroller';

export default MultiScroller;
